import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { 
  EyeOutlined, 
  SearchOutlined, 
  FilterOutlined,
  CalendarOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined,
  DownloadOutlined,
  BarChartOutlined,
  TeamOutlined,
  TrophyOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import dayjs from "dayjs";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ;
const API_BASE = `${API_BASE_URL}/api`;

const VisitorsPage = () => {
  const navigate = useNavigate();
  const printRef = useRef();
  
  // State for visitors data
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // KPI Counts
  const [todayCount, setTodayCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [customDateRange, setCustomDateRange] = useState({
    start: null,
    end: null
  });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filterByKPI, setFilterByKPI] = useState(null); // 'today', 'total', 'active', 'inactive'
  const [sortBy, setSortBy] = useState('logins'); // 'logins', 'date', 'name'
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' for top logins

  // Bulk delete states
  const [selectedVisitors, setSelectedVisitors] = useState([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  // Selected visitor details
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [visitorSessions, setVisitorSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Mobile responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // All users list toggle
  const [showAllUsers, setShowAllUsers] = useState(false);

  // Date options
  const dateOptions = [
    { value: "all", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last7days", label: "Last 7 Days" },
    { value: "last30days", label: "Last 30 Days" },
    { value: "last3months", label: "Last 3 Months" },
    { value: "last6months", label: "Last 6 Months" },
    { value: "last1year", label: "Last 1 Year" },
    { value: "custom", label: "Custom Range" }
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchVisitors();
  }, []);

  // Fetch all visitors from API
  const fetchVisitors = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE}/admin/visitors`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });

      console.log("✅ Visitors fetched:", response.data);
      
      if (response.data.success) {
        const visitorsData = response.data.visitors || [];
        setVisitors(visitorsData);
        calculateKPIs(visitorsData);
      } else {
        setError(response.data.error || "Failed to fetch visitors");
      }
    } catch (err) {
      console.error("❌ Error fetching visitors:", err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate all KPI counts
  const calculateKPIs = (visitorsData) => {
    const today = dayjs().format('YYYY-MM-DD');
    
    const todayCount = visitorsData.filter(v => 
      dayjs(v.created_at).format('YYYY-MM-DD') === today
    ).length;
    
    const totalCount = visitorsData.length;
    const activeCount = visitorsData.filter(v => v.status === 'active').length;
    const inactiveCount = visitorsData.filter(v => v.status === 'inactive' || v.status === 'blocked' || v.status === 'expired').length;
    
    setTodayCount(todayCount);
    setTotalCount(totalCount);
    setActiveCount(activeCount);
    setInactiveCount(inactiveCount);
  };

  // Handle KPI card click
  const handleKPIClick = (type) => {
    setFilterByKPI(type);
    setCurrentPage(1);
    setShowAllUsers(false); // Reset all users toggle
    setSelectedVisitors([]); // Clear selections
    
    // Reset other filters
    if (type === 'today') {
      setDateFilter('today');
      setStatusFilter('all');
    } else if (type === 'total') {
      setDateFilter('all');
      setStatusFilter('all');
    } else if (type === 'active') {
      setStatusFilter('active');
      setDateFilter('all');
    } else if (type === 'inactive') {
      setStatusFilter('inactive');
      setDateFilter('all');
    }
  };

  // Fetch visitor sessions
  const fetchVisitorSessions = async (visitorId) => {
    setLoadingSessions(true);
    try {
      const token = sessionStorage.getItem('token');
      
      const response = await axios.get(`${API_BASE}/visitor/sessions?visitor_id=${visitorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setVisitorSessions(response.data.sessions || []);
      } else {
        setVisitorSessions([]);
      }
    } catch (err) {
      console.error("Error fetching sessions:", err);
      setVisitorSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  // View visitor details
  const viewVisitorDetails = async (visitor) => {
    setSelectedVisitor(visitor);
    await fetchVisitorSessions(visitor.id);
    setShowDetailsModal(true);
  };

  // Update visitor status
  const updateVisitorStatus = async (visitorId, newStatus) => {
    if (!window.confirm(`Change status to ${newStatus}?`)) return;

    try {
      const token = sessionStorage.getItem('token');
      
      const response = await axios.put(`${API_BASE}/admin/visitor-status`, 
        { visitor_id: visitorId, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        alert(`✅ Status updated to ${newStatus}`);
        fetchVisitors();
        if (showDetailsModal) setShowDetailsModal(false);
      }
    } catch (err) {
      console.error("Error updating status:", err);
      alert("❌ Failed to update status");
    }
  };

  // ========== DELETE VISITOR FUNCTIONS ==========
  
  // Delete single visitor
  const deleteVisitor = async (visitorId, visitorName) => {
    if (!window.confirm(`Are you sure you want to delete "${visitorName}"? This action cannot be undone!`)) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      
      const response = await axios.delete(`${API_BASE}/admin/visitor/${visitorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert(`✅ Visitor "${visitorName}" deleted successfully`);
        fetchVisitors(); // Refresh the list
        if (showDetailsModal) setShowDetailsModal(false);
      } else {
        alert("❌ Failed to delete visitor: " + (response.data.error || "Unknown error"));
      }
    } catch (err) {
      console.error("Error deleting visitor:", err);
      alert("❌ Failed to delete visitor: " + (err.response?.data?.error || err.message));
    }
  };

  // Bulk delete visitors
  const bulkDeleteVisitors = async () => {
    if (selectedVisitors.length === 0) {
      alert("Please select at least one visitor to delete");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedVisitors.length} visitor(s)? This action cannot be undone!`)) {
      return;
    }

    setBulkDeleteLoading(true);
    let successCount = 0;
    let failCount = 0;
    const failedVisitors = [];

    for (const visitorId of selectedVisitors) {
      try {
        const token = sessionStorage.getItem('token');
        const response = await axios.delete(`${API_BASE}/admin/visitor/${visitorId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          successCount++;
        } else {
          failCount++;
          failedVisitors.push(visitorId);
        }
      } catch (err) {
        failCount++;
        failedVisitors.push(visitorId);
        console.error(`Failed to delete visitor ${visitorId}:`, err);
      }
    }

    if (successCount > 0) {
      alert(`✅ Successfully deleted ${successCount} visitor(s)`);
      if (failCount > 0) {
        alert(`⚠️ Failed to delete ${failCount} visitor(s): ${failedVisitors.join(', ')}`);
      }
      setSelectedVisitors([]);
      fetchVisitors(); // Refresh the list
    } else {
      alert("❌ Failed to delete visitors");
    }
    setBulkDeleteLoading(false);
  };

  // Handle select all checkbox
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const currentVisitorIds = displayedVisitors.map(v => v.id);
      setSelectedVisitors(currentVisitorIds);
    } else {
      setSelectedVisitors([]);
    }
  };

  // Handle single visitor selection
  const handleSelectVisitor = (visitorId) => {
    if (selectedVisitors.includes(visitorId)) {
      setSelectedVisitors(selectedVisitors.filter(id => id !== visitorId));
    } else {
      setSelectedVisitors([...selectedVisitors, visitorId]);
    }
  };

  // Filter and sort visitors
  const filteredAndSortedVisitors = useMemo(() => {
    let filtered = [...visitors];

    // Apply KPI filter if set
    if (filterByKPI === 'today') {
      const today = dayjs().format('YYYY-MM-DD');
      filtered = filtered.filter(v => 
        dayjs(v.created_at).format('YYYY-MM-DD') === today
      );
    } else if (filterByKPI === 'active') {
      filtered = filtered.filter(v => v.status === 'active');
    } else if (filterByKPI === 'inactive') {
      filtered = filtered.filter(v => v.status === 'inactive' || v.status === 'blocked' || v.status === 'expired');
    }

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(v => 
        v.username?.toLowerCase().includes(searchLower) ||
        v.full_name?.toLowerCase().includes(searchLower) ||
        v.email?.toLowerCase().includes(searchLower) ||
        v.phone?.includes(search) ||
        v.organization?.toLowerCase().includes(searchLower) ||
        v.purpose?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter (if not already filtered by KPI)
    if (statusFilter !== 'all' && filterByKPI !== 'active' && filterByKPI !== 'inactive') {
      filtered = filtered.filter(v => v.status === statusFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const today = dayjs();
      const yesterday = dayjs().subtract(1, 'day');
      
      filtered = filtered.filter(v => {
        const created = dayjs(v.created_at);
        
        switch(dateFilter) {
          case 'today':
            return created.isSame(today, 'day');
          case 'yesterday':
            return created.isSame(yesterday, 'day');
          case 'last7days':
            return created.isAfter(today.subtract(7, 'day'));
          case 'last30days':
            return created.isAfter(today.subtract(30, 'day'));
          case 'last3months':
            return created.isAfter(today.subtract(3, 'month'));
          case 'last6months':
            return created.isAfter(today.subtract(6, 'month'));
          case 'last1year':
            return created.isAfter(today.subtract(1, 'year'));
          case 'custom':
            if (customDateRange.start && customDateRange.end) {
              const start = dayjs(customDateRange.start);
              const end = dayjs(customDateRange.end);
              return created.isBetween(start, end, 'day', '[]');
            }
            return true;
          default:
            return true;
        }
      });
    }

    // Apply sorting
    if (sortBy === 'logins') {
      filtered.sort((a, b) => {
        const aLogins = a.login_count || 0;
        const bLogins = b.login_count || 0;
        return sortOrder === 'desc' ? bLogins - aLogins : aLogins - bLogins;
      });
    } else if (sortBy === 'date') {
      filtered.sort((a, b) => {
        const aDate = dayjs(a.created_at);
        const bDate = dayjs(b.created_at);
        return sortOrder === 'desc' ? bDate - aDate : aDate - bDate;
      });
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => {
        const aName = a.full_name || '';
        const bName = b.full_name || '';
        return sortOrder === 'asc' 
          ? aName.localeCompare(bName)
          : bName.localeCompare(aName);
      });
    }

    return filtered;
  }, [visitors, search, statusFilter, dateFilter, customDateRange, filterByKPI, sortBy, sortOrder]);

  // Get top 10 visitors by logins (default view)
  const topVisitors = useMemo(() => {
    return [...visitors]
      .sort((a, b) => (b.login_count || 0) - (a.login_count || 0))
      .slice(0, 10);
  }, [visitors]);

  // Determine which visitors to display
  const displayedVisitors = useMemo(() => {
    if (showAllUsers || search || statusFilter !== 'all' || dateFilter !== 'all' || filterByKPI) {
      const start = (currentPage - 1) * itemsPerPage;
      return filteredAndSortedVisitors.slice(start, start + itemsPerPage);
    } else {
      return topVisitors;
    }
  }, [filteredAndSortedVisitors, topVisitors, showAllUsers, search, statusFilter, dateFilter, filterByKPI, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedVisitors.length / itemsPerPage);

  // Handle date filter change
  const handleDateFilterChange = (e) => {
    const value = e.target.value;
    setDateFilter(value);
    setShowCustomDatePicker(value === 'custom');
    setCurrentPage(1);
    setShowAllUsers(true); // Switch to all users mode when filtering
    setSelectedVisitors([]); // Clear selections
  };

  // Handle search
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
    setSelectedVisitors([]); // Clear selections
    if (e.target.value) {
      setShowAllUsers(true); // Switch to all users when searching
    }
  };

  // Format date
  const formatDate = (date) => {
    return date ? dayjs(date).format('DD/MM/YYYY HH:mm') : 'N/A';
  };

  // Format date only
  const formatDateOnly = (date) => {
    return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
  };

  // Get status color
  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return '#28a745';
      case 'inactive': return '#ffc107';
      case 'blocked': return '#dc3545';
      case 'expired': return '#6c757d';
      default: return '#17a2b8';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch(status) {
      case 'active': return <CheckCircleOutlined />;
      case 'inactive': return <ClockCircleOutlined />;
      case 'blocked': return <CloseCircleOutlined />;
      case 'expired': return <ExclamationCircleOutlined />;
      default: return <UserOutlined />;
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const dataToExport = showAllUsers ? filteredAndSortedVisitors : topVisitors;
    const headers = ['ID', 'Username', 'Full Name', 'Email', 'Phone', 'Organization', 'Purpose', 'Status', 'Login Count', 'Last Login', 'Registered', 'Expiry'];
    
    const csvData = dataToExport.map(v => [
      v.id,
      v.username,
      v.full_name,
      v.email,
      v.phone,
      v.organization || '',
      v.purpose || '',
      v.status,
      v.login_count || 0,
      v.last_login ? formatDate(v.last_login) : 'Never',
      formatDate(v.created_at),
      v.account_expiry ? formatDate(v.account_expiry) : 'N/A'
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `visitors_report_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Print function
  const handlePrint = () => {
    const dataToPrint = showAllUsers ? filteredAndSortedVisitors : topVisitors;
    const printWindow = window.open('', '_blank');
    const today = dayjs().format('DD/MM/YYYY HH:mm');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Visitors Report - ${today}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #0d47a1; }
            .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
            .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
            .stat-card { background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #0d47a1; }
            .stat-label { color: #6c757d; font-size: 12px; }
            .stat-value { font-size: 28px; font-weight: bold; color: #0d47a1; }
            .top-badge { background: #ffc107; color: #856404; padding: 2px 8px; border-radius: 12px; font-size: 11px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #0d47a1; color: white; padding: 10px; text-align: left; }
            td { padding: 8px; border-bottom: 1px solid #e9ecef; }
            .status-badge { padding: 4px 8px; border-radius: 12px; font-size: 11px; }
            .footer { margin-top: 20px; text-align: center; color: #6c757d; font-size: 12px; }
            @media print {
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Visitors Report</h1>
            <div>Generated: ${today}</div>
          </div>
          
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-label">Today's Visitors</div>
              <div class="stat-value">${todayCount}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Total Visitors</div>
              <div class="stat-value">${totalCount}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Active</div>
              <div class="stat-value">${activeCount}</div>
            </div>
            <div class="stat-card">
              <div class="stat-label">Inactive</div>
              <div class="stat-value">${inactiveCount}</div>
            </div>
          </div>
          
          <h3>${showAllUsers ? 'All Visitors' : 'Top 10 Visitors by Logins'}</h3>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Logins</th>
                <th>Registered</th>
              </tr>
            </thead>
            <tbody>
              ${dataToPrint.map((v, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${v.full_name}</td>
                  <td>@${v.username}</td>
                  <td>${v.email}</td>
                  <td>${v.phone}</td>
                  <td><span class="status-badge" style="background: ${getStatusColor(v.status)}20; color: ${getStatusColor(v.status)}">${v.status}</span></td>
                  <td><strong>${v.login_count || 0}</strong> ${index < 3 ? '🏆' : ''}</td>
                  <td>${formatDate(v.created_at)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          <div class="footer">
            <p>© ${new Date().getFullYear()} Directorate of Municipal Administration</p>
            <p>This is a system generated report</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  // Styles (keeping your existing styles, adding new ones)
  const styles = {
    page: {
      padding: isMobile ? "60px 10px 20px 10px" : "80px 20px 20px 20px",
      minHeight: "100vh",
      background: "#f8f9fa"
    },
    container: {
      maxWidth: "1400px",
      margin: "0 auto"
    },
    header: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      justifyContent: "space-between",
      alignItems: isMobile ? "flex-start" : "center",
      marginBottom: "20px",
      background: "white",
      padding: "20px",
      borderRadius: "12px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
    },
    headerLeft: {
      marginBottom: isMobile ? "15px" : 0
    },
    headerTitle: {
      margin: "0 0 5px 0",
      color: "#2c3e50",
      fontSize: isMobile ? "20px" : "24px",
      display: "flex",
      alignItems: "center",
      gap: "10px"
    },
    headerSubtitle: {
      margin: 0,
      color: "#6c757d",
      fontSize: isMobile ? "13px" : "14px"
    },
    headerRight: {
      display: "flex",
      gap: "10px",
      flexWrap: "wrap"
    },
    kpiGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
      gap: "15px",
      marginBottom: "25px"
    },
    kpiCard: (color, clickable = true) => ({
      background: "white",
      padding: "20px",
      borderRadius: "12px",
      boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
      borderLeft: `4px solid ${color}`,
      cursor: clickable ? "pointer" : "default",
      transition: "transform 0.2s, box-shadow 0.2s",
      ":hover": clickable ? {
        transform: "translateY(-2px)",
        boxShadow: "0 8px 25px rgba(0,0,0,0.15)"
      } : {}
    }),
    kpiHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "10px"
    },
    kpiLabel: {
      color: "#6c757d",
      fontSize: "14px",
      fontWeight: "500"
    },
    kpiIcon: {
      fontSize: "24px",
      opacity: "0.7"
    },
    kpiValue: {
      fontSize: isMobile ? "28px" : "36px",
      fontWeight: "bold",
      color: "#2c3e50",
      lineHeight: "1"
    },
    kpiSubtext: {
      fontSize: "12px",
      color: "#6c757d",
      marginTop: "5px"
    },
    topVisitorsBadge: {
      background: "linear-gradient(135deg, #ffd700, #ffa500)",
      color: "#fff",
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "bold",
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      marginLeft: "10px"
    },
    filtersContainer: {
      background: "white",
      padding: "20px",
      borderRadius: "10px",
      marginBottom: "20px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
    },
    filtersRow: {
      display: "flex",
      flexDirection: isMobile ? "column" : "row",
      gap: "15px",
      alignItems: isMobile ? "stretch" : "center",
      flexWrap: "wrap"
    },
    searchBox: {
      flex: 2,
      position: "relative",
      minWidth: "250px"
    },
    searchIcon: {
      position: "absolute",
      left: "12px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#6c757d"
    },
    searchInput: {
      width: "100%",
      padding: "12px 12px 12px 40px",
      border: "1px solid #ced4da",
      borderRadius: "6px",
      fontSize: "14px",
      outline: "none",
      ":focus": {
        borderColor: "#0d47a1"
      }
    },
    filterSelect: {
      flex: 1,
      padding: "12px",
      border: "1px solid #ced4da",
      borderRadius: "6px",
      fontSize: "14px",
      background: "white",
      minWidth: "180px"
    },
    customDatePicker: {
      display: "flex",
      gap: "10px",
      flexWrap: "wrap",
      marginTop: "15px",
      padding: "15px",
      background: "#f8f9fa",
      borderRadius: "6px"
    },
    dateInput: {
      flex: 1,
      padding: "10px",
      border: "1px solid #ced4da",
      borderRadius: "4px",
      minWidth: "150px"
    },
    actionButtons: {
      display: "flex",
      gap: "10px",
      marginLeft: "auto"
    },
    primaryButton: {
      padding: "10px 20px",
      background: "#0d47a1",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px"
    },
    successButton: {
      padding: "10px 20px",
      background: "#28a745",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px"
    },
    warningButton: {
      padding: "10px 20px",
      background: "#ffc107",
      color: "#856404",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px"
    },
    dangerButton: {
      padding: "10px 20px",
      background: "#dc3545",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "14px"
    },
    secondaryButton: {
      padding: "10px 20px",
      background: "#6c757d",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer"
    },
    tableContainer: {
      background: "white",
      borderRadius: "10px",
      overflow: "auto",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      minWidth: isMobile ? "1400px" : "100%"
    },
    th: {
      padding: "15px",
      textAlign: "left",
      background: "#f8f9fa",
      borderBottom: "2px solid #e9ecef",
      fontSize: isMobile ? "12px" : "14px",
      fontWeight: 600,
      color: "#495057"
    },
    td: {
      padding: "15px",
      borderBottom: "1px solid #e9ecef",
      fontSize: isMobile ? "12px" : "14px"
    },
    rankBadge: (rank) => ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "24px",
      height: "24px",
      borderRadius: "50%",
      background: rank === 1 ? "#ffd700" : rank === 2 ? "#c0c0c0" : rank === 3 ? "#cd7f32" : "#f8f9fa",
      color: rank <= 3 ? "#fff" : "#6c757d",
      fontWeight: "bold",
      fontSize: "12px",
      marginRight: "8px"
    }),
    statusBadge: (color) => ({
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      padding: "4px 10px",
      background: `${color}20`,
      color: color,
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: 500
    }),
    actionButton: {
      padding: "6px 12px",
      background: "#0d47a1",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "12px",
      marginRight: "5px"
    },
    viewButton: {
      padding: "6px 12px",
      background: "#17a2b8",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "12px",
      marginRight: "5px"
    },
    deleteButton: {
      padding: "6px 12px",
      background: "#dc3545",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "12px"
    },
    pagination: {
      display: "flex",
      justifyContent: "center",
      gap: "10px",
      marginTop: "20px",
      flexWrap: "wrap"
    },
    pageButton: (active) => ({
      padding: "8px 12px",
      background: active ? "#0d47a1" : "white",
      color: active ? "white" : "#0d47a1",
      border: "1px solid #0d47a1",
      borderRadius: "4px",
      cursor: "pointer",
      minWidth: "40px"
    }),
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000
    },
    modalContent: {
      background: "white",
      borderRadius: "12px",
      padding: "30px",
      maxWidth: "800px",
      width: "90%",
      maxHeight: "90vh",
      overflow: "auto"
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px"
    },
    modalTitle: {
      margin: 0,
      color: "#2c3e50"
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: "24px",
      cursor: "pointer",
      color: "#6c757d"
    },
    infoGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: "15px",
      marginBottom: "20px"
    },
    infoItem: {
      padding: "15px",
      background: "#f8f9fa",
      borderRadius: "8px"
    },
    infoLabel: {
      fontSize: "12px",
      color: "#6c757d",
      marginBottom: "5px",
      display: "flex",
      alignItems: "center",
      gap: "5px"
    },
    infoValue: {
      fontSize: "16px",
      fontWeight: 500,
      color: "#2c3e50"
    },
    sessionsTable: {
      width: "100%",
      borderCollapse: "collapse",
      marginTop: "15px"
    },
    sessionsTh: {
      padding: "10px",
      textAlign: "left",
      background: "#f8f9fa",
      fontSize: "12px"
    },
    sessionsTd: {
      padding: "10px",
      borderBottom: "1px solid #e9ecef",
      fontSize: "12px"
    },
    allUsersToggle: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "15px",
      padding: "10px",
      background: "#e3f2fd",
      borderRadius: "6px",
      flexWrap: "wrap"
    },
    toggleButton: {
      padding: "8px 16px",
      background: showAllUsers ? "#0d47a1" : "#f8f9fa",
      color: showAllUsers ? "white" : "#0d47a1",
      border: "1px solid #0d47a1",
      borderRadius: "4px",
      cursor: "pointer",
      fontWeight: 500
    },
    bulkDeleteBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 15px",
      background: "#fff3cd",
      border: "1px solid #ffeaa7",
      borderRadius: "6px",
      marginBottom: "15px",
      flexWrap: "wrap",
      gap: "10px"
    }
  };

  if (loading && visitors.length === 0) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={{ textAlign: "center", padding: "50px" }}>
            <div style={{ fontSize: "48px", marginBottom: "20px" }}>⏳</div>
            <h3>Loading visitors data...</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <h1 style={styles.headerTitle}>
              <TeamOutlined /> Visitors Management
              {!showAllUsers && !search && statusFilter === 'all' && dateFilter === 'all' && !filterByKPI && (
                <span style={styles.topVisitorsBadge}>
                  <TrophyOutlined /> Top 10 by Logins
                </span>
              )}
            </h1>
            <p style={styles.headerSubtitle}>
              {!showAllUsers && !search && statusFilter === 'all' && dateFilter === 'all' && !filterByKPI
                ? 'Showing top 10 visitors with highest login count'
                : 'View and manage all visitor registrations and their activity'
              }
            </p>
          </div>
          <div style={styles.headerRight}>
            <button
              onClick={exportToCSV}
              style={styles.successButton}
            >
              <DownloadOutlined /> Export CSV
            </button>
            <button
              onClick={handlePrint}
              style={styles.warningButton}
            >
              <PrinterOutlined /> Print Report
            </button>
            <button
              onClick={() => navigate(-1)}
              style={styles.secondaryButton}
            >
              ← Back
            </button>
          </div>
        </div>

        {/* KPI Cards - Clickable */}
        <div style={styles.kpiGrid}>
          <div 
            style={styles.kpiCard('#ff9800', true)}
            onClick={() => handleKPIClick('today')}
          >
            <div style={styles.kpiHeader}>
              <span style={styles.kpiLabel}>Today's Visitors</span>
              <CalendarOutlined style={styles.kpiIcon} />
            </div>
            <div style={styles.kpiValue}>{todayCount}</div>
            <div style={styles.kpiSubtext}>Click to view today's visitors</div>
          </div>

          <div 
            style={styles.kpiCard('#0d47a1', true)}
            onClick={() => handleKPIClick('total')}
          >
            <div style={styles.kpiHeader}>
              <span style={styles.kpiLabel}>Total Visitors</span>
              <TeamOutlined style={styles.kpiIcon} />
            </div>
            <div style={styles.kpiValue}>{totalCount}</div>
            <div style={styles.kpiSubtext}>Click to view all</div>
          </div>

          <div 
            style={styles.kpiCard('#28a745', true)}
            onClick={() => handleKPIClick('active')}
          >
            <div style={styles.kpiHeader}>
              <span style={styles.kpiLabel}>Active</span>
              <CheckCircleOutlined style={styles.kpiIcon} />
            </div>
            <div style={styles.kpiValue}>{activeCount}</div>
            <div style={styles.kpiSubtext}>Click to view active</div>
          </div>

          <div 
            style={styles.kpiCard('#dc3545', true)}
            onClick={() => handleKPIClick('inactive')}
          >
            <div style={styles.kpiHeader}>
              <span style={styles.kpiLabel}>Inactive/Blocked</span>
              <CloseCircleOutlined style={styles.kpiIcon} />
            </div>
            <div style={styles.kpiValue}>{inactiveCount}</div>
            <div style={styles.kpiSubtext}>Click to view inactive</div>
          </div>
        </div>

        {/* Bulk Delete Bar */}
        {selectedVisitors.length > 0 && (
          <div style={styles.bulkDeleteBar}>
            <div>
              <strong>{selectedVisitors.length}</strong> visitor(s) selected
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setSelectedVisitors([])}
                style={{ padding: "6px 12px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
              >
                Clear Selection
              </button>
              <button
                onClick={bulkDeleteVisitors}
                disabled={bulkDeleteLoading}
                style={styles.dangerButton}
              >
                {bulkDeleteLoading ? "Deleting..." : <><DeleteOutlined /> Delete Selected ({selectedVisitors.length})</>}
              </button>
            </div>
          </div>
        )}

        {/* All Users Toggle */}
        <div style={styles.allUsersToggle}>
          <span>
            <TeamOutlined /> View Mode:
          </span>
          <button
            style={styles.toggleButton}
            onClick={() => {
              setShowAllUsers(!showAllUsers);
              setCurrentPage(1);
              setSelectedVisitors([]);
              if (!showAllUsers) {
                setFilterByKPI(null);
              }
            }}
          >
            {showAllUsers ? 'Showing All Users' : 'Show All Users'}
          </button>
          {!showAllUsers && !search && statusFilter === 'all' && dateFilter === 'all' && !filterByKPI && (
            <span style={{ color: '#28a745', marginLeft: '10px' }}>
              <TrophyOutlined /> Displaying top 10 visitors by login count
            </span>
          )}
        </div>

        {/* Filters */}
        <div style={styles.filtersContainer}>
          {isMobile && (
            <button
              style={{
                width: "100%",
                padding: "12px",
                background: "#f8f9fa",
                border: "1px solid #ced4da",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                cursor: "pointer",
                marginBottom: "10px"
              }}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <FilterOutlined /> {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
          )}

          <div style={styles.filtersRow}>
            <div style={styles.searchBox}>
              <SearchOutlined style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search by name, email, phone, organization..."
                value={search}
                onChange={handleSearch}
                style={styles.searchInput}
              />
            </div>

            {(!isMobile || showMobileFilters) && (
              <>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setFilterByKPI(null);
                    setCurrentPage(1);
                    setShowAllUsers(true);
                    setSelectedVisitors([]);
                  }}
                  style={styles.filterSelect}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="blocked">Blocked</option>
                  <option value="expired">Expired</option>
                </select>

                <select
                  value={dateFilter}
                  onChange={handleDateFilterChange}
                  style={styles.filterSelect}
                >
                  {dateOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  style={styles.filterSelect}
                >
                  <option value="logins">Sort by Logins</option>
                  <option value="date">Sort by Date</option>
                  <option value="name">Sort by Name</option>
                </select>

                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  style={styles.filterSelect}
                >
                  <option value="desc">Highest First</option>
                  <option value="asc">Lowest First</option>
                </select>
              </>
            )}
          </div>

          {/* Custom Date Range Picker */}
          {showCustomDatePicker && (
            <div style={styles.customDatePicker}>
              <input
                type="date"
                style={styles.dateInput}
                value={customDateRange.start || ''}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                placeholder="Start Date"
              />
              <input
                type="date"
                style={styles.dateInput}
                value={customDateRange.end || ''}
                onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                placeholder="End Date"
              />
              <button
                onClick={() => {
                  setCustomDateRange({ start: null, end: null });
                  setDateFilter('all');
                  setShowCustomDatePicker(false);
                }}
                style={{ padding: "10px 15px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Results count */}
        <div style={{ marginBottom: "10px", fontSize: "14px", color: "#6c757d" }}>
          Showing {displayedVisitors.length} of {
            showAllUsers ? filteredAndSortedVisitors.length : topVisitors.length
          } visitors
          {!showAllUsers && !search && statusFilter === 'all' && dateFilter === 'all' && !filterByKPI && (
            <span style={{ marginLeft: "10px", color: "#28a745" }}>
              (Top 10 by login count)
            </span>
          )}
        </div>

        {/* Visitors Table */}
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: "40px" }}>
                  <input
                    type="checkbox"
                    checked={selectedVisitors.length === displayedVisitors.length && displayedVisitors.length > 0}
                    onChange={handleSelectAll}
                  />
                </th>
                <th style={styles.th}>#</th>
                <th style={styles.th}>User</th>
                <th style={styles.th}>Contact</th>
                <th style={styles.th}>Organization</th>
                <th style={styles.th}>Purpose</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Logins</th>
                <th style={styles.th}>Last Active</th>
                <th style={styles.th}>Registered</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedVisitors.map((visitor, index) => {
                const rank = !showAllUsers && !search && statusFilter === 'all' && dateFilter === 'all' && !filterByKPI 
                  ? index + 1 
                  : null;
                const isSelected = selectedVisitors.includes(visitor.id);
                
                return (
                  <tr key={visitor.id} style={{ background: isSelected ? "#e6f7ff" : "transparent" }}>
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectVisitor(visitor.id)}
                      />
                    </td>
                    <td style={styles.td}>
                      {rank ? (
                        <span style={styles.rankBadge(rank)}>
                          {rank}
                        </span>
                      ) : (
                        <span style={{ color: "#6c757d" }}>•</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{visitor.full_name}</div>
                        <div style={{ fontSize: "11px", color: "#6c757d" }}>
                          @{visitor.username}
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div>
                        <div><MailOutlined style={{ marginRight: "5px" }} /> {visitor.email}</div>
                        <div style={{ fontSize: "11px", marginTop: "4px" }}>
                          <PhoneOutlined /> {visitor.phone}
                        </div>
                      </div>
                    </td>
                    <td style={styles.td}>{visitor.organization || 'N/A'}</td>
                    <td style={styles.td}>{visitor.purpose || 'N/A'}</td>
                    <td style={styles.td}>
                      <span style={styles.statusBadge(getStatusColor(visitor.status))}>
                        {getStatusIcon(visitor.status)} {visitor.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        background: visitor.login_count > 0 ? "#e8f5e9" : "#f8f9fa",
                        padding: "4px 8px",
                        borderRadius: "12px",
                        fontWeight: 600,
                        fontSize: "14px"
                      }}>
                        {visitor.login_count || 0}
                        {rank === 1 && <span style={{ marginLeft: "5px" }}>🏆</span>}
                        {rank === 2 && <span style={{ marginLeft: "5px" }}>🥈</span>}
                        {rank === 3 && <span style={{ marginLeft: "5px" }}>🥉</span>}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {visitor.last_login ? formatDate(visitor.last_login) : 'Never'}
                    </td>
                    <td style={styles.td}>{formatDateOnly(visitor.created_at)}</td>
                    <td style={styles.td}>
                      <button
                        onClick={() => viewVisitorDetails(visitor)}
                        style={styles.viewButton}
                      >
                        <EyeOutlined /> View
                      </button>
                      <button
                        onClick={() => deleteVisitor(visitor.id, visitor.full_name || visitor.username)}
                        style={styles.deleteButton}
                      >
                        <DeleteOutlined /> Delete
                      </button>
                    </td>
                  </tr>
                );
              })}

              {displayedVisitors.length === 0 && (
                <tr>
                  <td colSpan="11" style={{ textAlign: "center", padding: "50px" }}>
                    <div style={{ fontSize: "48px", marginBottom: "20px" }}>👤</div>
                    <h3>No visitors found</h3>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {showAllUsers && totalPages > 1 && (
          <div style={styles.pagination}>
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={styles.pageButton(false)}
            >
              ←
            </button>
            
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  style={styles.pageButton(currentPage === pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              style={styles.pageButton(false)}
            >
              →
            </button>
          </div>
        )}

        {/* Visitor Details Modal - Add Delete button */}
        {showDetailsModal && selectedVisitor && (
          <div style={styles.modal} onClick={() => setShowDetailsModal(false)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h2 style={styles.modalTitle}>Visitor Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  style={styles.closeButton}
                >
                  ×
                </button>
              </div>

              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}><UserOutlined /> Full Name</div>
                  <div style={styles.infoValue}>{selectedVisitor.full_name}</div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}><UserOutlined /> Username</div>
                  <div style={styles.infoValue}>@{selectedVisitor.username}</div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}><MailOutlined /> Email</div>
                  <div style={styles.infoValue}>{selectedVisitor.email}</div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}><PhoneOutlined /> Phone</div>
                  <div style={styles.infoValue}>{selectedVisitor.phone}</div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}><ClockCircleOutlined /> Status</div>
                  <div style={styles.infoValue}>
                    <span style={styles.statusBadge(getStatusColor(selectedVisitor.status))}>
                      {getStatusIcon(selectedVisitor.status)} {selectedVisitor.status}
                    </span>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}><CalendarOutlined /> Registered</div>
                  <div style={styles.infoValue}>{formatDate(selectedVisitor.created_at)}</div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}><ClockCircleOutlined /> Expires</div>
                  <div style={styles.infoValue}>
                    {selectedVisitor.account_expiry ? formatDate(selectedVisitor.account_expiry) : 'N/A'}
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}><EyeOutlined /> Total Logins</div>
                  <div style={styles.infoValue}>
                    <span style={{ fontSize: "20px", fontWeight: "bold", color: "#0d47a1" }}>
                      {selectedVisitor.login_count || 0}
                    </span>
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}><ClockCircleOutlined /> Total Time</div>
                  <div style={styles.infoValue}>
                    {Math.floor((selectedVisitor.total_session_time || 0) / 60)} minutes
                  </div>
                </div>
                <div style={styles.infoItem}>
                  <div style={styles.infoLabel}><CheckCircleOutlined /> Last Login</div>
                  <div style={styles.infoValue}>
                    {selectedVisitor.last_login ? formatDate(selectedVisitor.last_login) : 'Never'}
                  </div>
                </div>
                {selectedVisitor.organization && (
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>🏢 Organization</div>
                    <div style={styles.infoValue}>{selectedVisitor.organization}</div>
                  </div>
                )}
                {selectedVisitor.purpose && (
                  <div style={styles.infoItem}>
                    <div style={styles.infoLabel}>📝 Purpose</div>
                    <div style={styles.infoValue}>{selectedVisitor.purpose}</div>
                  </div>
                )}
              </div>

              {/* Sessions History */}
              <h3 style={{ margin: "20px 0 10px" }}>Session History</h3>
              {loadingSessions ? (
                <div style={{ textAlign: "center", padding: "20px" }}>Loading sessions...</div>
              ) : visitorSessions.length > 0 ? (
                <table style={styles.sessionsTable}>
                  <thead>
                    <tr>
                      <th style={styles.sessionsTh}>Login Time</th>
                      <th style={styles.sessionsTh}>Logout Time</th>
                      <th style={styles.sessionsTh}>Duration</th>
                      <th style={styles.sessionsTh}>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitorSessions.map((session) => (
                      <tr key={session.id}>
                        <td style={styles.sessionsTd}>{formatDate(session.login_time)}</td>
                        <td style={styles.sessionsTd}>
                          {session.logout_time ? formatDate(session.logout_time) : 'Active'}
                        </td>
                        <td style={styles.sessionsTd}>
                          {session.session_duration 
                            ? `${Math.floor(session.session_duration / 60)} min ${session.session_duration % 60} sec`
                            : 'In progress'}
                        </td>
                        <td style={styles.sessionsTd}>{session.ip_address || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: "#6c757d", textAlign: "center", padding: "20px" }}>
                  No sessions found
                </p>
              )}

              {/* Action Buttons with Delete */}
              <div style={{ display: "flex", gap: "10px", marginTop: "20px", flexWrap: "wrap" }}>
                {selectedVisitor.status === 'active' && (
                  <button
                    onClick={() => updateVisitorStatus(selectedVisitor.id, 'blocked')}
                    style={{...styles.actionButton, background: '#dc3545'}}
                  >
                    Block Visitor
                  </button>
                )}
                {selectedVisitor.status === 'blocked' && (
                  <button
                    onClick={() => updateVisitorStatus(selectedVisitor.id, 'active')}
                    style={{...styles.actionButton, background: '#28a745'}}
                  >
                    Unblock Visitor
                  </button>
                )}
                {selectedVisitor.status === 'inactive' && (
                  <button
                    onClick={() => updateVisitorStatus(selectedVisitor.id, 'active')}
                    style={{...styles.actionButton, background: '#28a745'}}
                  >
                    Activate Visitor
                  </button>
                )}
                <button
                  onClick={() => deleteVisitor(selectedVisitor.id, selectedVisitor.full_name || selectedVisitor.username)}
                  style={{...styles.deleteButton, padding: "6px 12px"}}
                >
                  <DeleteOutlined /> Delete Permanently
                </button>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  style={{...styles.actionButton, background: '#6c757d'}}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitorsPage;